import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import axios from "../config/axios";
import toast from "react-hot-toast";

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Create Auth Context
const AuthContext = createContext({
  user: null,
  userProfile: null,
  loading: true,
  signIn: () => { },
  logOut: () => { },
  refreshProfile: () => { },
  devLogin: () => { },
  devRegister: () => { },
});

/**
 * Configure axios defaults for authentication
 */
const setupAxiosInterceptors = (idToken) => {
  if (idToken) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

/**
 * Auth Provider Component
 * Manages Firebase authentication state and user profile
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch user profile from backend
   */
  const fetchUserProfile = async (firebaseUser) => {
    try {
      const idToken = await firebaseUser.getIdToken();
      setupAxiosInterceptors(idToken);

      const response = await axios.get("/api/auth/me");
      setUserProfile(response.data);
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      // toast.error("Failed to load user profile");
    }
  };

  /**
   * Sign in with Google
   */
  const signIn = async () => {
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();

      // Request additional scopes for Google integrations
      provider.addScope("https://www.googleapis.com/auth/userinfo.profile");
      provider.addScope("https://www.googleapis.com/auth/userinfo.email");

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      if (user) {
        await fetchUserProfile(user);
        toast.success(`Welcome back, ${user.displayName || user.email}!`);
      }
    } catch (error) {
      console.error("Sign in error:", error);

      if (error.code === "auth/popup-closed-by-user") {
        toast.error("Sign in was cancelled");
      } else if (error.code === "auth/popup-blocked") {
        toast.error("Popup was blocked. Please allow popups and try again.");
      } else {
        toast.error("Failed to sign in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out user
   */
  const logOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      setupAxiosInterceptors(null);
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out");
    }
  };

  /**
   * Refresh user profile data
   */
  const refreshProfile = async () => {
    if (user) {
      await fetchUserProfile(user);
    }
  };

  /**
   * Development login with email/password
   */
  const devLogin = async (email, password) => {
    try {
      setLoading(true);

      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Get the ID token and set it immediately
      const idToken = await user.getIdToken();
      setupAxiosInterceptors(idToken);

      // Set the user state
      setUser(user);

      // Fetch user profile from backend
      try {
        const response = await axios.get("/api/auth/me");
        setUserProfile(response.data);
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        // If profile doesn't exist, it will be created
        setUserProfile(null);
      }

      toast.success(`Welcome back, ${user.displayName || user.email}!`);
    } catch (error) {
      console.error("Login error:", error);

      let message = "Login failed";
      if (error.code === "auth/user-not-found") {
        message = "No account found with this email";
      } else if (error.code === "auth/wrong-password") {
        message = "Incorrect password";
      } else if (error.code === "auth/invalid-email") {
        message = "Invalid email address";
      } else if (error.code === "auth/user-disabled") {
        message = "This account has been disabled";
      }

      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register with email/password using Firebase
   */
  const devRegister = async (email, password, displayName) => {
    try {
      setLoading(true);

      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Update the user's display name
      if (displayName) {
        await updateProfile(user, {
          displayName: displayName,
        });
      }

      // Get the ID token and set it immediately
      const idToken = await user.getIdToken();
      setupAxiosInterceptors(idToken);

      // Set the user state
      setUser(user);

      // Create user profile in backend
      try {
        const response = await axios.get("/api/auth/me");
        setUserProfile(response.data);
      } catch (error) {
        console.error("Failed to create user profile:", error);
        // Profile creation might fail on first attempt, that's okay
        // It will be created on next login
      }

      toast.success(`Welcome to CRO Agent, ${displayName || email}!`);
    } catch (error) {
      console.error("Registration error:", error);

      let message = "Registration failed";
      if (error.code === "auth/email-already-in-use") {
        message = "Email is already registered";
      } else if (error.code === "auth/weak-password") {
        message = "Password should be at least 6 characters";
      } else if (error.code === "auth/invalid-email") {
        message = "Invalid email address";
      }

      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initialize authentication state
   */
  useEffect(() => {
    // Monitor Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          setUser(firebaseUser);
          await fetchUserProfile(firebaseUser);
        } else {
          setUser(null);
          setUserProfile(null);
          setupAxiosInterceptors(null);
        }
      } catch (error) {
        console.error("Auth state change error:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  /**
   * Set up token refresh for long-running sessions
   */
  useEffect(() => {
    let refreshInterval;

    if (user) {
      // Refresh token every 50 minutes (Firebase tokens expire after 1 hour)
      refreshInterval = setInterval(
        async () => {
          try {
            const idToken = await user.getIdToken(true); // Force refresh
            setupAxiosInterceptors(idToken);
          } catch (error) {
            console.error("Token refresh error:", error);
          }
        },
        50 * 60 * 1000,
      );
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user]);

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    logOut,
    refreshProfile,
    devLogin,
    devRegister,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
